import React, { useEffect, useRef, useState } from "react";
import { MessageCircle, X, Send, Sparkles, ExternalLink, Globe, Home, Copy, ThumbsUp, ThumbsDown, Share2, Check } from "lucide-react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { AnimatePresence, motion } from "framer-motion";
import { useLang } from "@/context/LanguageContext";
import { useCurrency } from "@/context/CurrencyContext";
import { api, API_BASE } from "@/lib/api";
import { getSessionId, getVisitId } from "@/lib/device";
import PhoneWithCountry, { isPhoneValid } from "@/components/forms/PhoneWithCountry";
import { pushDL } from "@/lib/analytics";
import RoniInstallModal from "@/components/chat/RoniInstallModal"; // legacy, kept as file, not routed. Component unused.
import ShareRoniNudge from "@/components/chat/ShareRoniNudge";

const SHARE_NUDGE_THRESHOLD = 6; // user turns before Roni asks for a share

// -------------------------------------------------------------------
// MessageActions — copy / like / dislike / share bar under each
// assistant reply. Feedback fires into /api/chat/feedback so we can
// see thumbs-up / thumbs-down rates in the CRM analytics.
// -------------------------------------------------------------------
function MessageActions({ text, sessionId, messageIndex, lang }) {
  const [copied, setCopied] = useState(false);
  const [feedback, setFeedback] = useState(null); // "like" | "dislike" | null
  const isAR = lang === "ar";

  // Strip [[CTA_INLINE]] markers and HTML from copied/shared text
  const clean = (text || "")
    .replace(/\[\[CTA_INLINE\]\][\s\S]*?\[\[\/CTA_INLINE\]\]/g, "")
    .replace(/<[^>]+>/g, "")
    .replace(/\*\*/g, "")
    .trim();

  const onCopy = async () => {
    try {
      await navigator.clipboard.writeText(clean);
      setCopied(true);
      toast.success(isAR ? "تم نسخ الإجابة" : "Answer copied");
      setTimeout(() => setCopied(false), 1800);
      try { pushDL("roni_msg_copy", { session_id: sessionId, msg_idx: messageIndex }); } catch { /* noop */ }
    } catch {
      toast.error(isAR ? "تعذّر النسخ" : "Copy failed");
    }
  };

  const onFeedback = async (kind) => {
    if (feedback === kind) return; // don't double-fire
    setFeedback(kind);
    try {
      await api.post("/chat/feedback", {
        session_id: sessionId,
        message_index: messageIndex,
        feedback: kind,
        snippet: clean.slice(0, 500),
      });
      toast.success(isAR
        ? (kind === "like" ? "شكراً على تقييمكم" : "شكراً, سنعمل على التحسين")
        : (kind === "like" ? "Thanks for the feedback" : "Thanks, we'll improve"));
      try { pushDL(`roni_msg_${kind}`, { session_id: sessionId, msg_idx: messageIndex }); } catch { /* noop */ }
    } catch {
      /* silently fail — analytics only */
    }
  };

  const onShare = async () => {
    const suffix = isAR
      ? "\n\n, عبر بروبيرتك · thepropertic.ae/aiagent"
      : "\n\n, via The Propertic · thepropertic.ae/aiagent";
    const shareText = clean + suffix;
    try {
      if (navigator.share) {
        await navigator.share({
          title: isAR ? "روني · بروبيرتك" : "Roni · The Propertic",
          text: shareText,
          url: "https://thepropertic.ae/aiagent",
        });
        try { pushDL("roni_msg_share", { session_id: sessionId, msg_idx: messageIndex, channel: "native" }); } catch { /* noop */ }
      } else {
        // Fallback: WhatsApp share
        const wa = `https://wa.me/?text=${encodeURIComponent(shareText)}`;
        window.open(wa, "_blank", "noopener,noreferrer");
        try { pushDL("roni_msg_share", { session_id: sessionId, msg_idx: messageIndex, channel: "whatsapp_fallback" }); } catch { /* noop */ }
      }
    } catch { /* user cancelled — no-op */ }
  };

  return (
    <div className="flex items-center gap-1 mt-2.5 pt-2" data-testid="chat-msg-actions">
      <button
        onClick={onCopy}
        data-testid="chat-msg-copy"
        aria-label={isAR ? "نسخ" : "Copy"}
        className="h-8 w-8 inline-flex items-center justify-center rounded-full hover:bg-propertic-sand text-propertic-muted hover:text-propertic-navy transition-colors"
        title={isAR ? "نسخ الإجابة" : "Copy answer"}
      >
        {copied ? <Check className="w-3.5 h-3.5 text-emerald-600" /> : <Copy className="w-3.5 h-3.5" />}
      </button>
      <button
        onClick={() => onFeedback("like")}
        data-testid="chat-msg-like"
        aria-label={isAR ? "أعجبني" : "Like"}
        className={`h-8 w-8 inline-flex items-center justify-center rounded-full transition-colors ${feedback === "like" ? "bg-propertic-gold/15 text-propertic-gold" : "hover:bg-propertic-sand text-propertic-muted hover:text-propertic-navy"}`}
        title={isAR ? "إجابة مفيدة" : "Helpful"}
      >
        <ThumbsUp className="w-3.5 h-3.5" />
      </button>
      <button
        onClick={() => onFeedback("dislike")}
        data-testid="chat-msg-dislike"
        aria-label={isAR ? "لم يعجبني" : "Dislike"}
        className={`h-8 w-8 inline-flex items-center justify-center rounded-full transition-colors ${feedback === "dislike" ? "bg-red-100 text-red-600" : "hover:bg-propertic-sand text-propertic-muted hover:text-propertic-navy"}`}
        title={isAR ? "غير مفيد" : "Not helpful"}
      >
        <ThumbsDown className="w-3.5 h-3.5" />
      </button>
      <button
        onClick={onShare}
        data-testid="chat-msg-share"
        aria-label={isAR ? "مشاركة" : "Share"}
        className="h-8 w-8 inline-flex items-center justify-center rounded-full hover:bg-propertic-sand text-propertic-muted hover:text-propertic-navy transition-colors"
        title={isAR ? "مشاركة" : "Share"}
      >
        <Share2 className="w-3.5 h-3.5" />
      </button>
    </div>
  );
}


const GREETING_DISMISS_KEY = "propertic_roni_greeting_dismissed";
const TOOLTIP_CYCLE_MS = 6000;
const IDLE_TYPING_MS = 20000;
const FIRST_GREETING_MS = 8000;

// Matches the trailing legal Note footer the LLM appends per the
// UNIVERSAL_DISCLAIMER_FOOTER prompt rule (EN + AR variants). We
// strip it from message bodies at render-time so the consultation
// form / CTA card can slide BETWEEN the answer and the Note, keeping
// the Note visually last per product rules.
const NOTE_RE = /\n*(?:---|\*\*\*|___)\s*\n\s*\*?(?:Note|ملاحظة)\s*[:：][\s\S]*?\.\s*\*?\s*$/;

function extractNote(text) {
  if (!text) return { body: "", note: null };
  const m = text.match(NOTE_RE);
  if (!m) return { body: text, note: null };
  // Same defensive comma-space fix we apply inside the main body.
  // Roni occasionally emits "legal,tax,or immigration" with no
  // spaces after commas; injecting one keeps the disclaimer legible.
  const noteRaw = m[0]
    .replace(/^\n*(?:---|\*\*\*|___)\s*\n\s*/, "")
    .replace(/^\*|\*$/g, "")
    .trim();
  const note = noteRaw.replace(/,([A-Za-z\u0600-\u065F\u066E-\u06EF\u06FA-\u06FF])/g, ", $1");
  return {
    body: text.slice(0, m.index).replace(/\s+$/, ""),
    note,
  };
}

function stripNote(text) {
  return extractNote(text).body;
}

function renderMd(text) {
  if (!text) return "";
  let html = text
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#39;");
  // Convert the [[CTA_INLINE]]...[[/CTA_INLINE]] marker the model emits at
  // the end of low-intent nudges + contact-consultant intent turns into a
  // bold, gold, underlined inline link. Clicking it reveals the consultation
  // form (see `onClick` handler on the message container). Handles the
  // HTML-escaped form because we ran the .replace(/</g,"&lt;") pass above.
  // Repair a closing marker the model occasionally truncates ("[[/ …" or missing).
  if (html.includes("[[CTA_INLINE]]") && !html.includes("[[/CTA_INLINE]]")) {
    html = /\[\[\/(?!CTA_INLINE\]\])/.test(html)
      ? html.replace(/\[\[\/(?!CTA_INLINE\]\])\s*/, "[[/CTA_INLINE]] ")
      : html.replace(/(\[\[CTA_INLINE\]\][^\n]*)/, "$1[[/CTA_INLINE]]");
  }
  html = html.replace(
    /(?:\*\*)?\[\[CTA_INLINE\]\]([\s\S]+?)\[\[\/CTA_INLINE\]\](?:\*\*)?/g,
    '<a href="#" data-inline-cta="consultation" class="inline-consultation-cta">$1</a>'
  ).replace(/\[\[\/?CTA_INLINE\]\]/g, "");
  // Strip any consultation / WhatsApp / phone / booking lines that occasionally
  // leak into the reply body, the CTA block below (cta_mode) always renders
  // the correct button (premium form OR WhatsApp), so repeating them inline
  // creates the duplicated CTA the user flagged (Turn 3 Gemini test).
  html = html
    // Kill any markdown link that targets /#consultation, in any language.
    .replace(/^.*\[[^\]]*\]\((?:https?:[^)]*)?\/?#consultation\)[^\n]*$/gim, "")
    // Kill inline standalone /#consultation URLs.
    .replace(/\(?\bhttps?:\/\/[^\s)]*\/#consultation\b[^\s)]*\)?/gi, "")
    .replace(/\(?\/#consultation\b\)?/gi, "")
    // Kill lines that are just call-to-action button text (English + Arabic).
    .replace(/^\s*[-*•>]?\s*(?:📱|📞|💬|📝|📌|📇|✨)?\s*(?:\*\*)?\s*(?:Book (?:here|my consultation|your (?:free )?consultation)(?:\s+now)?|Free Consultation|Schedule (?:a|your) consultation|Get in touch|Contact us|احجزوا?\s+استشارتكم?|احجز(?:\s+استشارتك)?|استشارة\s+مجانية)(?:\s*(?:—|-|,|now)?[^\n]*)?(?:\*\*)?\s*$/gim, "")
    // Kill "WhatsApp: +971 …" and "Email/Phone/Tel" contact-label lines.
    .replace(/^\s*[-*•>]?\s*(?:📱|📞|💬|📝|📌|📇)?\s*(?:\*\*)?\s*WhatsApp(?:\*\*)?\s*[:\-—]\s*[+\d\s()-]+.*$/gim, "")
    .replace(/^\s*(?:\*\*)?(?:Email|Phone|Tel|هاتف|بريد)(?:\*\*)?\s*:.*$/gim, "")
    // Orphan list/heading markers and empty emphasis left by the model (R2 A2).
    .replace(/^\s*(?:[-*•+]|#{1,6})\s*$/gm, "")
    .replace(/\*\*\s*\*\*/g, "")
    .replace(/\bAED(?=\d)/g, "AED ")
    // Markdown bullets ("* item") → "• item"; drop stray single '*' at line ends.
    .replace(/^\s*\*\s+(?=\S)/gm, "• ")
    .replace(/^.*$/gm, (ln) => ((ln.replace(/\*\*/g, "").match(/\*/g) || []).length % 2 === 1 ? ln.replace(/\*\s*$/, "") : ln))
    // Collapse triple+ blank lines that result from the strips above.
    .replace(/\n{3,}/g, "\n\n");
  // Universal AED thousands-separator normaliser. Handles every LLM
  // chunk-boundary glitch the user has flagged: "AED 80000",
  // "AED 1000,000", "AED 1,203634", "AED 1129200". Also idempotent
  // on already-correct figures ("AED 1,203,634" → "AED 1,203,634").
  // Only touches AED-prefixed Latin-digit amounts. Arabic-Indic
  // digits (٠-٩) are deliberately left alone for RTL safety. Preserves
  // decimals (e.g., "AED 1,693.9" stays as-is).
  html = html.replace(/(AED\s+)(\d[\d,]*(?:\.\d+)?)\b/g, (match, prefix, num) => {
    const parts = num.replace(/,/g, "").split(".");
    const intPart = parts[0];
    if (intPart.length < 4) return match;
    const grouped = intPart.replace(/\B(?=(\d{3})+(?!\d))/g, ",");
    return prefix + grouped + (parts[1] !== undefined ? "." + parts[1] : "");
  });
  // Insert a space after any comma that's immediately followed by a
  // letter (Latin or Arabic). Fixes Roni's occasional "legal,tax,or
  // immigration" chunk-glitch without touching numeric commas like
  // "1,000,000" (digit-after-comma is preserved).
  // Comma directly followed by a letter gets a space; Arabic-Indic/Persian digits and
  // Arabic separators (U+0660-066D, U+06F0-06F9) are excluded so ١,٢٠٣ stays grouped (R2-AI-04).
  html = html.replace(/,([A-Za-z\u0600-\u065F\u066E-\u06EF\u06FA-\u06FF])/g, ", $1");
  const lines = html.split("\n");
  const out = [];
  let inTable = false;
  let inBlockquote = false;
  const closeBlockquote = () => { if (inBlockquote) { out.push("</blockquote>"); inBlockquote = false; } };
  for (let i = 0; i < lines.length; i++) {
    let line = lines[i];
    const isTableRow = /^\s*\|.*\|\s*$/.test(line);
    const isSep = /^\s*\|?[\s|:-]+\|?\s*$/.test(line);
    if (isTableRow && !inTable) { closeBlockquote(); inTable = true; out.push("<div class='chat-table-wrap'><table class='chat-table'>"); }
    if (inTable && !isTableRow) { inTable = false; out.push("</table></div>"); }
    if (isTableRow) {
      if (isSep) continue;
      const cells = line.trim().replace(/^\||\|$/g, "").split("|").map((c) => c.trim());
      const isHeader = i + 1 < lines.length && /^\s*\|?[\s|:-]+\|?\s*$/.test(lines[i + 1]);
      const tag = isHeader ? "th" : "td";
      out.push("<tr>" + cells.map((c) => {
        // Table cells frequently contain bare numeric AED amounts
        // (e.g. "80000", "~4,200") because Roni puts the currency
        // in the column header, not the cell. Apply the same
        // thousands-separator normaliser here so tables render
        // consistently with inline AED figures. Guards: years
        // 1900-2099 are left as-is; alphanumeric tokens (e.g. "4.00%",
        // "W37") are skipped by the digit-only capture.
        const grouped = c.replace(/(^|[^\d.,])(\d{4,})(?=$|[^\d.,])/g, (m, pre, num) => {
          const n = parseInt(num, 10);
          if (num.length === 4 && n >= 1900 && n <= 2099) return m;
          return pre + num.replace(/\B(?=(\d{3})+(?!\d))/g, ",");
        });
        return `<${tag}>${grouped}</${tag}>`;
      }).join("") + "</tr>");
      continue;
    }
    // Horizontal rule --- / *** / ___
    if (/^\s*(?:---+|\*\*\*+|___+)\s*$/.test(line)) { closeBlockquote(); out.push("<hr class='chat-hr' />"); continue; }
    // Headings ### / ## / #
    const h = line.match(/^\s*(#{1,6})\s+(.+?)\s*#*\s*$/);
    if (h) { closeBlockquote(); const lvl = Math.min(h[1].length + 2, 6); out.push(`<h${lvl} class='chat-h${lvl}'>${h[2]}</h${lvl}>`); continue; }
    // Blockquote (escaped &gt; because we HTML-escaped earlier)
    const bq = line.match(/^\s*&gt;\s?(.*)$/);
    if (bq) {
      if (!inBlockquote) { inBlockquote = true; out.push("<blockquote class='chat-quote'>"); }
      out.push(bq[1] + "<br/>");
      continue;
    } else {
      closeBlockquote();
    }
    out.push(line);
  }
  if (inTable) out.push("</table></div>");
  closeBlockquote();
  html = out.join("\n");
  html = html
    .replace(/\*\*(.+?)\*\*/g, "<strong>$1</strong>")
    .replace(/(^|[^\w*])\*(?!\s)([^*\n]+?)\*(?!\w)/g, "$1<em>$2</em>")
    .replace(/`([^`]+)`/g, "<code>$1</code>")
    // Any leftover unpaired "**" is markup noise, never content.
    .replace(/\*\*/g, "");
  // Guarantee readable spacing between back-to-back bold tokens.
  // Markdown "**income****nationality**" renders as
  // "<strong>income</strong><strong>nationality</strong>" which the
  // browser paints as one visually merged word. Injecting a space
  // between adjacent strong tags restores natural word breaks
  // without changing meaning.
  html = html.replace(/<\/strong>(<strong>)/g, "</strong> $1");
  html = html.replace(/^\s*[-•] (.+)$/gm, "<li>$1</li>");
  html = html.replace(/(<li>[\s\S]+?<\/li>)/g, (m) => `<ul>${m}</ul>`);
  // URL is restricted to characters that cannot close the attribute (quotes are already entity-encoded above).
  html = html.replace(/\[([^\]]+)\]\((https?:\/\/[^\s)"'<>&]+(?:&amp;[^\s)"'<>&]+)*)\)/g, '<a href="$2" target="_blank" rel="noopener noreferrer" class="chat-link">$1</a>');
  // Wrap prose in <p>; skip blocks that already start with a tag
  return html.split(/\n{2,}/).map((p) => (/^\s*<(?:h\d|table|hr|blockquote|ul|ol|p|div)/i.test(p) ? p : `<p>${p.replace(/\n/g, "<br/>")}</p>`)).join("");
}

export default function AIChatWidget({ alwaysOpen = false } = {}) {
  const { t, lang, setLang, lh } = useLang();
  const { currency } = useCurrency();
  const [open, setOpen] = useState(alwaysOpen);
  const [sessionId, setSessionId] = useState(null);
  const [messages, setMessages] = useState([]);
  const [groundingByMsg, setGroundingByMsg] = useState({}); // index -> [{title,uri}]
  const [toolByMsg, setToolByMsg] = useState({}); // index -> {tool, label_en, label_ar}
  const [input, setInput] = useState("");
  const [streaming, setStreaming] = useState(false);
  const [chatError, setChatError] = useState(false);
  const [showCTA, setShowCTA] = useState(false);
  const [showInstallModal, setShowInstallModal] = useState(false);
  // Track how many user turns have been sent so we can inject the WhatsApp
  // "share Roni" nudge after 6 questions, and remove it if the user keeps
  // typing instead of tapping share.
  const [userTurns, setUserTurns] = useState(0);
  const [shareNudgeShown, setShareNudgeShown] = useState(false);
  const _RoniInstallModal = RoniInstallModal; // silence unused-import warning

  // Detect if Roni is running as an installed PWA (standalone display mode).
  // The AR/EN language toggle only makes sense inside the installed PWA, on
  // the regular website the visitor already has the global header toggle, so
  // showing it inside the chat is redundant clutter.
  const [isStandalonePwa, setIsStandalonePwa] = useState(false);
  useEffect(() => {
    if (typeof window === "undefined") return;
    const check = () => {
      const mm = window.matchMedia && window.matchMedia("(display-mode: standalone)").matches;
      const ios = window.navigator && window.navigator.standalone === true;
      setIsStandalonePwa(Boolean(mm || ios));
    };
    check();
    const mql = window.matchMedia && window.matchMedia("(display-mode: standalone)");
    if (mql && mql.addEventListener) {
      mql.addEventListener("change", check);
      return () => mql.removeEventListener("change", check);
    }
    return undefined;
  }, []);

  // "Add Roni to Home Screen" CTA, instead of opening a modal, we inject a
  const [_showInstallModalLegacy] = useState(false); void _showInstallModalLegacy;
  // Roni assistant message into the chat with platform-detected install
  // instructions. Also fires an analytics event so we can count how many
  // visitors tapped the install CTA (visible in CRM → Statistics).
  const handleInstallCtaClick = () => {
    // Analytics: fire /track/click on the backend and gtag/dataLayer.
    try { api.post("/track/click", { type: "roni_install_cta", path: window.location.pathname }); } catch (_e) { /* noop */ }
    try { pushDL("roni_install_cta_click", { source: "chat_widget" }); } catch (_e) { /* noop */ }
    // Only add ONE install message at a time. If one already exists, keep it.
    setMessages((m) => {
      if (m.some((x) => x.role === "roni_install")) return m;
      const ua = navigator.userAgent || "";
      const platform = /iPad|iPhone|iPod/.test(ua) ? "ios" : /Android/i.test(ua) ? "android" : "desktop";
      const canFireNativePrompt = platform !== "ios" && !!window.__roniInstallEvt;
      return [...m, { role: "roni_install", platform, canFireNativePrompt }];
    });
  };

  // Fire the cached beforeinstallprompt event (Android/Chrome/Edge only).
  const confirmNativeInstall = async () => {
    const evt = window.__roniInstallEvt;
    if (!evt) {
      toast.error(lang === "ar"
        ? "المتصفح لا يدعم التثبيت التلقائي. جرّب Chrome أو Edge."
        : "Browser install prompt not available. Try Chrome or Edge.");
      return;
    }
    try {
      evt.prompt();
      const choice = await evt.userChoice;
      window.__roniInstallEvt = null;
      if (choice.outcome === "accepted") {
        toast.success(lang === "ar" ? "تمت الإضافة إلى الشاشة الرئيسية." : "Added to home screen.");
        setMessages((m) => m.filter((x) => x.role !== "roni_install"));
      }
    } catch { /* noop */ }
  };
  // "none" | "lead_form" | "whatsapp", driven by backend `event: done`
  // lead_form  = premium consultation form + small WhatsApp hint under it
  // whatsapp   = server-side analytics label ONLY; UI renders nothing
  // none       = plain conversation, no CTA UI at all
  const [ctaMode, setCtaMode] = useState("none");
  const [showLead, setShowLead] = useState(false);
  const [leadForm, setLeadForm] = useState({ full_name: "", phone: "" });
  const [leadAgreed, setLeadAgreed] = useState(false);

  // Roni "star of the page" state
  const [tooltipIdx, setTooltipIdx] = useState(0);
  const [showIdleTyping, setShowIdleTyping] = useState(false);
  const [showGreeting, setShowGreeting] = useState(false);
  const [avatarFallback, setAvatarFallback] = useState(false);

  const tooltipPhrases = t("chat.tooltip_cycle") || [t("chat.title")];

  // Cycle tooltip every 6s (only while the chat is closed).
  useEffect(() => {
    if (open) return;
    const id = setInterval(() => {
      setTooltipIdx((i) => (i + 1) % (tooltipPhrases.length || 1));
    }, TOOLTIP_CYCLE_MS);
    return () => clearInterval(id);
  }, [open, tooltipPhrases.length]);

  // Idle detection, after 20s of no scroll or pointermove, show a typing
  // bubble above Roni. Any activity resets the timer.
  useEffect(() => {
    if (open) { setShowIdleTyping(false); return; }
    let timer;
    const reset = () => {
      setShowIdleTyping(false);
      clearTimeout(timer);
      timer = setTimeout(() => setShowIdleTyping(true), IDLE_TYPING_MS);
    };
    reset();
    window.addEventListener("scroll", reset, { passive: true });
    window.addEventListener("pointermove", reset, { passive: true });
    window.addEventListener("touchstart", reset, { passive: true });
    return () => {
      clearTimeout(timer);
      window.removeEventListener("scroll", reset);
      window.removeEventListener("pointermove", reset);
      window.removeEventListener("touchstart", reset);
    };
  }, [open]);

  // First-visit greeting bubble, appears once per session after 8s. Dismissible
  // and remembered via sessionStorage so it doesn't nag returning visitors
  // in the same session.
  useEffect(() => {
    if (open) { setShowGreeting(false); return; }
    let dismissed = false;
    try { dismissed = sessionStorage.getItem(GREETING_DISMISS_KEY) === "1"; } catch { /* ignore */ }
    if (dismissed) return;
    const id = setTimeout(() => setShowGreeting(true), FIRST_GREETING_MS);
    return () => clearTimeout(id);
  }, [open]);

  const dismissGreeting = () => {
    setShowGreeting(false);
    try { sessionStorage.setItem(GREETING_DISMISS_KEY, "1"); } catch { /* ignore */ }
  };
  const openFromGreeting = () => {
    dismissGreeting();
    setOpen(true);
  };

  // Deep-link + programmatic open support.
  // - Legacy #ai_agent / #ai-agent hash (kept for backward compat with any
  //   external sitelinks, ads, and Google sitelinks already indexed).
  // - "roni:open" custom event, opens the panel silently (no auto-question).
  // - "roni:ask" custom event with { text }, opens the panel AND submits
  //   the question so Roni streams the answer immediately. Fired from
  //   the /aiagent capability-map buttons.
  //
  // Uses a ref to sendMessage so the stable listener always calls the
  // latest closure without re-subscribing on every state change.
  const sendMessageRef = useRef(null);
  useEffect(() => {
    const openIfHash = () => {
      const h = (window.location.hash || "").toLowerCase();
      if (h === "#ai_agent" || h === "#ai-agent") {
        dismissGreeting();
        setOpen(true);
      }
    };
    const openFromEvent = () => {
      dismissGreeting();
      setOpen(true);
    };
    const askFromEvent = (e) => {
      const text = e?.detail?.text;
      dismissGreeting();
      setOpen(true);
      if (text && sendMessageRef.current) {
        // Small defer so the panel mount + opener message render first,
        // then Roni receives the question and starts streaming.
        setTimeout(() => sendMessageRef.current(text), 60);
      }
    };
    openIfHash();
    window.addEventListener("hashchange", openIfHash);
    window.addEventListener("roni:open", openFromEvent);
    window.addEventListener("roni:ask", askFromEvent);
    return () => {
      window.removeEventListener("hashchange", openIfHash);
      window.removeEventListener("roni:open", openFromEvent);
      window.removeEventListener("roni:ask", askFromEvent);
    };
    // eslint-disable-next-line
  }, []);

  // Auto-open Roni after 30s of session, soft nudge, once per session,
  // cancelled if user opens the chat themselves or is actively typing.
  useEffect(() => {
    let opened = false;
    try { opened = sessionStorage.getItem("propertic_roni_auto_shown") === "1"; } catch { /* ignore */ }
    if (opened || open) return;
    let cancelled = false;
    const cancelOnActivity = () => { cancelled = true; };
    document.addEventListener("keydown", cancelOnActivity, { once: true });
    const timer = setTimeout(() => {
      if (cancelled) return;
      try { sessionStorage.setItem("propertic_roni_auto_shown", "1"); } catch { /* ignore */ }
      dismissGreeting();
      setOpen(true);
    }, 90000);
    return () => {
      clearTimeout(timer);
      document.removeEventListener("keydown", cancelOnActivity);
    };
    // eslint-disable-next-line
  }, []);

  // Sync body class + iOS scroll-lock so overlays (WhatsApp, back-to-top,
  // tooltip) can hide themselves cleanly while the chat panel is open.
  useEffect(() => {
    if (open) document.body.classList.add("chat-open");
    else document.body.classList.remove("chat-open");
    return () => document.body.classList.remove("chat-open");
  }, [open]);
  const bottomRef = useRef(null);

  useEffect(() => {
    if (open && messages.length === 0) setMessages([{ role: "assistant", content: t("chat.opener") }]);
    // eslint-disable-next-line
  }, [open]);
  useEffect(() => {
    if (open && messages.length <= 1) setMessages([{ role: "assistant", content: t("chat.opener") }]);
    // eslint-disable-next-line
  }, [lang]);
  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages, streaming]);

  const sendMessage = async (rawText) => {
    const text = (rawText || "").trim();
    if (!text || streaming) return;
    setInput("");
    // Strip any lingering "share_roni" nudge, the visitor typed a new
    // question instead of tapping share, so the nudge is no longer relevant.
    const nextTurn = userTurns + 1;
    setMessages((m) => {
      const cleaned = m.filter((x) => x.role !== "share_roni");
      return [...cleaned, { role: "user", content: text }, { role: "assistant", content: "" }];
    });
    setUserTurns(nextTurn);
    setStreaming(true);
    setChatError(false);
    setShowCTA(false);
    // Auto-hide the consultation container on the next question. The user
    // has an inline [[CTA_INLINE]] anchor in every substantive reply now,
    // so leaving the static container around after they've moved on adds
    // clutter. It'll reappear only if the backend fires cta_mode again.
    setCtaMode("none");
    setShowLead(false);

    try {
      const res = await fetch(`${API_BASE}/chat/send`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ session_id: sessionId, message: text, lang, active_currency: currency, device_id: getSessionId(), visit_id: getVisitId() }),
      });
      if (!res.ok || !res.body) throw new Error("stream failed");

      const reader = res.body.getReader();
      const decoder = new TextDecoder();
      let buffer = "";
      while (true) {
        const { value, done } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });
        const parts = buffer.split("\n\n");
        buffer = parts.pop() || "";
        for (const raw of parts) {
          const evLine = raw.split("\n").find((l) => l.startsWith("event:"));
          const dataLine = raw.split("\n").find((l) => l.startsWith("data:"));
          if (!dataLine) continue;
          const data = dataLine.replace(/^data:\s?/, "");
          const evName = evLine?.replace(/^event:\s?/, "") || "message";
          if (evName === "session") setSessionId(data);
          else if (evName === "grounding") {
            try {
              const parsed = JSON.parse(data);
              setMessages((mm) => {
                setGroundingByMsg((gm) => ({ ...gm, [mm.length - 1]: parsed.sources || [] }));
                return mm;
              });
            } catch { /* noop */ }
          } else if (evName === "tool_used") {
            try {
              const parsed = JSON.parse(data);
              setMessages((mm) => {
                setToolByMsg((tm) => ({ ...tm, [mm.length - 1]: parsed }));
                return mm;
              });
            } catch { /* noop */ }
          } else if (evName === "done") {
            try {
              const parsed = JSON.parse(data);
              // New routing: cta_mode drives which single CTA block renders.
              // Backward-compat: if old {cta: true} arrives, treat as lead_form.
              if (parsed.cta_mode) {
                setCtaMode(parsed.cta_mode);
                setShowCTA(parsed.cta_mode !== "none");
              } else if (parsed.cta) {
                setCtaMode("lead_form");
                setShowCTA(true);
              }
            } catch { /* noop */ }
          } else if (evName === "error") {
            const serverMsg = (data || "").trim();
            if (serverMsg) {
              setMessages((m) => {
                const copy = [...m];
                if (!(copy[copy.length - 1]?.content)) copy[copy.length - 1] = { role: "assistant", content: serverMsg };
                return copy;
              });
            }
            toast.error(lang === "ar" ? "حدث خطأ في المستشار الذكي. حاول مجدداً." : "AI advisor error. Please try again.");
            setChatError(true);
          }
          else {
            const chunk = data.replace(/\\n/g, "\n");
            setMessages((m) => {
              const copy = [...m];
              copy[copy.length - 1] = { role: "assistant", content: (copy[copy.length - 1].content || "") + chunk };
              return copy;
            });
          }
        }
      }
    } catch {
      setChatError(true);
      setMessages((m) => {
        const copy = [...m];
        copy[copy.length - 1] = { role: "assistant", content: "Sorry, I couldn't reach the advisor stream. Please try again." };
        return copy;
      });
    } finally {
      setStreaming(false);
      // Inject the WhatsApp share nudge once we hit the threshold. It sits
      // right after Roni's reply and disappears on the next user turn.
      if (nextTurn >= SHARE_NUDGE_THRESHOLD && !shareNudgeShown) {
        setMessages((m) => [...m, { role: "share_roni", content: "" }]);
        setShareNudgeShown(true);
      }
    }
  };

  // Keep the ref pointing at the latest sendMessage closure so the
  // stable "roni:ask" listener can invoke it without re-subscribing.
  useEffect(() => { sendMessageRef.current = sendMessage; });

  const submitLead = async () => {
    if (!leadForm.full_name || !leadForm.full_name.trim()) { toast.error(lang === "ar" ? "أدخل اسمك الكامل." : "Please enter your full name."); return; }
    if (!isPhoneValid(leadForm.phone)) { toast.error(lang === "ar" ? "أدخل رقم هاتف صحيح (8 أرقام على الأقل)." : "Please enter a valid phone (min 8 digits)."); return; }
    if (!leadAgreed) { toast.error(t("consultation.agree_error")); return; }
    try {
      await api.post("/leads", {
        ...leadForm,
        source: "chatbot",
        chat_session_id: sessionId,
        message: messages.slice(-8).map((m) => `${m.role}: ${m.content}`).join("\n---\n"),
      });
      pushDL("form_submit", { form_name: "chatbot_lead", form_id: "chat-lead-form" });
      pushDL("generate_lead", { form_name: "chatbot_lead" });
      setShowLead(false);
      setShowCTA(false);
      setLeadAgreed(false);
      toast.success(t("consultation.success"));
    } catch {
      toast.error(lang === "ar" ? "حدث خطأ ما." : "Something went wrong.");
    }
  };

  const suggestions = [t("chat.suggestion_1"), t("chat.suggestion_2"), t("chat.suggestion_3"), t("chat.suggestion_4")];

  return (
    <>
      <style>{`.chat-link { color: #B08543; text-decoration: underline; text-underline-offset: 2px; } .chat-link:hover { color: #C99B54; }`}</style>

      {/* Cycling ambient tooltip, swaps every 6s so Roni teaches visitors
          what to ask. Hidden on mobile to preserve the CTA space. */}
      <div className="fixed z-40 bottom-24 end-6 pointer-events-none ai-support-tooltip hidden sm:block">
        {!open && (
          <div className="absolute -top-9 end-1 whitespace-nowrap px-3 py-1.5 rounded-full bg-propertic-navy text-propertic-paper text-[10px] font-mono-brand tracking-[0.16em] uppercase shadow-lg overflow-hidden">
            <AnimatePresence mode="wait">
              <motion.span
                key={tooltipIdx}
                initial={{ opacity: 0, y: 6 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -6 }}
                transition={{ duration: 0.35 }}
                className="inline-block"
              >
                {tooltipPhrases[tooltipIdx] || t("chat.title")}
              </motion.span>
            </AnimatePresence>
            <span className="absolute -bottom-1 end-6 w-2 h-2 rotate-45 bg-propertic-navy" />
          </div>
        )}
      </div>

      {/* Idle typing indicator, 20s of no activity, sits above the launcher
          like Roni is about to speak. */}
      <AnimatePresence>
        {!open && showIdleTyping && !showGreeting && (
          <motion.div
            key="idle-typing"
            initial={{ opacity: 0, y: 8, scale: 0.9 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 8, scale: 0.9 }}
            transition={{ duration: 0.3 }}
            className="fixed z-40 bottom-24 end-6 pointer-events-none ai-support-tooltip hidden sm:block"
            data-testid="chat-idle-typing"
          >
            <div className="absolute -top-24 end-1 px-3.5 py-2.5 rounded-2xl rounded-ee-md bg-propertic-paper border hairline shadow-xl inline-flex items-center gap-1.5">
              <span className="typing-dot w-2 h-2 rounded-full bg-propertic-gold" />
              <span className="typing-dot w-2 h-2 rounded-full bg-propertic-gold" />
              <span className="typing-dot w-2 h-2 rounded-full bg-propertic-gold" />
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* First-visit greeting bubble, 8s after landing, once per session. */}
      <AnimatePresence>
        {!open && showGreeting && (
          <div
            className="fixed z-40 bottom-24 end-6 hidden sm:block"
            data-testid="roni-greeting"
          >
            <motion.div
              initial={{ opacity: 0, y: 12, scale: 0.9 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              exit={{ opacity: 0, y: 8, scale: 0.95 }}
              transition={{ type: "spring", stiffness: 260, damping: 22 }}
              className="absolute -top-3 end-0 -translate-y-full w-[300px] bg-propertic-paper border border-propertic-gold/40 shadow-[0_24px_60px_rgba(10,37,64,0.22)] rounded-2xl rounded-ee-md p-4"
            >
              <div className="flex items-start gap-3">
                <div className="w-10 h-10 rounded-full bg-gradient-to-br from-propertic-gold-2 to-propertic-gold grid place-items-center shrink-0 shadow-inner">
                  <span className="font-editorial text-propertic-navy text-base leading-none">R</span>
                </div>
                <div className="min-w-0 flex-1">
                  <p className="font-mono-brand text-propertic-gold text-[9px] tracking-[0.22em] uppercase mb-1">{t("chat.advisor_name")} · {t("chat.title")}</p>
                  <p className="text-[13px] text-propertic-ink leading-[1.45]">{t("chat.greeting_bubble")}</p>
                </div>
                <button
                  onClick={dismissGreeting}
                  data-testid="roni-greeting-dismiss"
                  aria-label={t("chat.greeting_dismiss")}
                  className="shrink-0 w-6 h-6 grid place-items-center rounded-full hover:bg-propertic-sand/60 text-propertic-muted hover:text-propertic-navy transition-colors"
                >
                  <X className="w-3.5 h-3.5" />
                </button>
              </div>
              <button
                onClick={openFromGreeting}
                data-testid="roni-greeting-open"
                className="mt-3 w-full h-9 rounded-full bg-propertic-navy hover:bg-propertic-navy-2 text-propertic-paper font-mono-brand text-[10px] tracking-[0.22em] uppercase transition-colors inline-flex items-center justify-center gap-2"
              >
                <Sparkles className="w-3 h-3" />
                {t("chat.greeting_open")}
              </button>
              <span className="absolute -bottom-2 end-8 w-3 h-3 rotate-45 bg-propertic-paper border-b border-e border-propertic-gold/40" />
            </motion.div>
          </div>
        )}
      </AnimatePresence>

      <button
        onClick={(e) => {
          // R2-RTL-01: if the floating launcher sits over a form field, hand the tap to the field.
          if (!open && e.clientX) {
            const under = document.elementsFromPoint(e.clientX, e.clientY)
              .find((el) => el !== e.currentTarget && !e.currentTarget.contains(el) && el.matches?.("input, select, textarea"));
            if (under) { under.focus(); return; }
          }
          dismissGreeting(); setOpen((v) => !v);
        }}
        data-testid="chat-toggle-btn"
        aria-label={t("chat.title")}
        className={`fixed z-40 bottom-4 end-4 h-12 w-12 sm:bottom-6 sm:end-6 sm:h-16 sm:w-16 rounded-full grid place-items-center transition-all duration-500 ${
          alwaysOpen ? "hidden" : ""
        } ${
          open
            ? "bg-propertic-paper border hairline text-propertic-navy shadow-[0_16px_40px_rgba(10,37,64,0.22)]"
            : "launcher-breathe text-propertic-navy"
        }`}
      >
        {open ? <X className="w-5 h-5" /> : (
          <MessageCircle className="w-5 h-5" strokeWidth={2} />
        )}
      </button>

      {open && (
        <div
          className={
            alwaysOpen
              ? "absolute inset-0 bg-propertic-paper-2 flex flex-col overflow-hidden"
              : "fixed z-50 inset-x-3 bottom-3 sm:inset-x-auto sm:bottom-24 sm:end-6 sm:w-[440px] lg:w-[min(58vw,820px)] xl:w-[min(56vw,900px)] h-[calc(100dvh-1.5rem)] sm:h-[78vh] sm:max-h-[720px] lg:h-[86vh] lg:max-h-[860px] bg-propertic-paper-2 rounded-[1.5rem] sm:rounded-[1.75rem] border hairline flex flex-col shadow-[0_32px_80px_rgba(10,37,64,0.28)] overflow-hidden"
          }
          data-testid="chat-panel"
        >
          {!alwaysOpen && (
          <div className="flex items-center justify-between px-4 sm:px-5 py-3.5 sm:py-4 bg-propertic-navy text-propertic-paper">
            <div className="flex items-center gap-3 min-w-0">
              {/* Roni AI Agent avatar, gold gradient "R" with a live
                  emerald "online" ping. Purely visual, no photo. */}
              <div className="relative shrink-0">
                <div className="w-10 h-10 sm:w-11 sm:h-11 rounded-full bg-gradient-to-br from-propertic-gold-2 to-propertic-gold border border-propertic-gold/60 grid place-items-center shadow-inner">
                  <span className="font-editorial text-propertic-navy text-lg leading-none">R</span>
                </div>
                <span className="absolute -bottom-0.5 -end-0.5 w-3 h-3 rounded-full bg-emerald-400 ring-2 ring-propertic-navy">
                  <span className="absolute inset-0 rounded-full bg-emerald-400 animate-ping opacity-70" />
                </span>
              </div>
              <div className="min-w-0">
                <div className="flex items-center gap-1.5">
                  <span className="font-editorial text-base sm:text-lg leading-tight truncate">{t("chat.advisor_name")}</span>
                </div>
                <p className="font-mono-brand text-propertic-gold-2/80 text-[9px] sm:text-[10px] tracking-[0.14em] mt-0.5 truncate">
                  <span className="inline-flex items-center gap-1">
                    <span className="w-1 h-1 rounded-full bg-emerald-400" />
                    {t("chat.online_now")}
                  </span>
                  <span className="mx-1.5 opacity-40">·</span>
                  <span>{t("chat.advisor_role")}</span>
                </p>
              </div>
            </div>
            <div className="flex items-center gap-1.5 shrink-0">
              {/* AR/EN toggle, only shown inside the installed PWA (standalone
                  mode). On the regular website visitors use the global header
                  language switcher, so this would be redundant. Flag mirrors
                  the target language: 🇬🇧 when tapping switches to English,
                  🇦🇪 when tapping switches to Arabic. */}
              {isStandalonePwa && (
                <button
                  onClick={() => setLang(lang === "ar" ? "en" : "ar")}
                  data-testid="chat-lang-toggle"
                  aria-label={lang === "ar" ? "Switch to English" : "التبديل إلى العربية"}
                  className="inline-flex items-center gap-1.5 rounded-full border border-propertic-gold/40 bg-propertic-paper/10 hover:bg-propertic-paper/20 h-8 px-2.5 transition-colors"
                >
                  <span aria-hidden className="text-sm leading-none">
                    {lang === "ar" ? "🇬🇧" : "🇦🇪"}
                  </span>
                  <span className="font-mono-brand text-propertic-paper text-[10px] tracking-[0.18em] uppercase">
                    {lang === "ar" ? "EN" : "AR"}
                  </span>
                </button>
              )}
              {!alwaysOpen && (
                <button
                  onClick={() => setOpen(false)}
                  data-testid="chat-close-btn"
                  aria-label={lang === "ar" ? "إغلاق المحادثة" : "Close chat"}
                  className="w-9 h-9 grid place-items-center rounded-full bg-propertic-paper/10 hover:bg-propertic-paper/20 text-propertic-paper transition-colors"
                >
                  <X className="w-4 h-4" />
                </button>
              )}
            </div>
          </div>
          )}

          {/* Add Roni to Home Screen CTA, placed on the off-white body
              background, right above the chat stream. Clicking it doesn't
              open a modal; instead it injects a Roni "answer" into the
              chat with platform-detected install instructions. Hidden if
              already running as an installed PWA. */}
          {typeof window !== "undefined" &&
            !(window.matchMedia && window.matchMedia("(display-mode: standalone)").matches) &&
            !window.navigator.standalone && (
            <button
              onClick={handleInstallCtaClick}
              data-testid="roni-install-cta"
              className="mx-4 sm:mx-5 mt-4 mb-1 inline-flex items-center justify-center gap-2 rounded-full border border-propertic-gold/50 bg-propertic-sand/70 hover:bg-propertic-sand text-propertic-navy font-mono-brand text-[10px] sm:text-[11px] tracking-[0.2em] uppercase px-4 h-9 transition-colors self-start"
            >
              <Home className="w-3 h-3 text-propertic-gold" />
              <span>{lang === "ar" ? "أضف روني إلى الشاشة الرئيسية" : "Add Roni to Home Screen"}</span>
            </button>
          )}

          <div className="flex-1 overflow-y-auto px-5 py-5 space-y-5">
            {/* We strip the "*Note: ..." legal footer out of every assistant
                message here at render-time so the CTA / consultation form
                can slide BETWEEN the answer body and the Note. Per product
                rules the Note is always the visually last block in the
                thread, regardless of how many CTA cards or forms mount
                after the message. Rendered once at the bottom below. */}
            {messages.map((m, i) => (
              <div key={i} data-testid={`chat-msg-${m.role}-${i}`}>
                {m.role === "user" ? (
                  <div className="ms-auto max-w-[85%] bg-propertic-navy text-white text-sm lg:text-base leading-relaxed px-4 py-3 rounded-2xl rounded-ee-md">{m.content}</div>
                ) : m.role === "share_roni" ? (
                  <ShareRoniNudge
                    lang={lang}
                    onDismiss={() => setMessages((mm) => mm.filter((x) => x.role !== "share_roni"))}
                  />
                ) : m.role === "roni_install" ? (
                  <RoniInstallMessage
                    lang={lang}
                    platform={m.platform}
                    canFireNativePrompt={m.canFireNativePrompt}
                    onConfirm={confirmNativeInstall}
                    onDismiss={() => setMessages((mm) => mm.filter((x) => x.role !== "roni_install"))}
                  />
                ) : (
                  <div>
                    {/* Internal-tool "Consulted:" pill removed, the visible
                        "Live sources" section at the bottom of the message
                        already communicates provenance, and the pill fired
                        awkwardly on off-topic questions that happened to
                        mention an area name (e.g. Palm Jumeirah in a BVI
                        question). Tool call still runs on the backend
                        for prompt injection, this is UI-only. */}
                    <div
                      className="text-sm lg:text-base text-propertic-ink/95 leading-relaxed chat-md"
                      onClick={(e) => {
                        // Intercept clicks on the inline [[CTA_INLINE]] link
                        // that renderMd converts to a <a data-inline-cta>.
                        const t = e.target.closest("a[data-inline-cta='consultation']");
                        if (!t) return;
                        e.preventDefault();
                        setShowLead(true);
                        try { pushDL("roni_inline_cta_click", { source: "chat_widget" }); } catch (_e) { /* noop */ }
                        // Scroll the freshly-mounted form into view.
                        setTimeout(() => { bottomRef.current?.scrollIntoView({ behavior: "smooth" }); }, 100);
                      }}
                      dangerouslySetInnerHTML={{
                        __html: renderMd(stripNote(m.content)) || (streaming && i === messages.length - 1 ? `<em style="color:#6E6A62">${t("chat.thinking")}</em>` : ""),
                      }}
                    />
                    {groundingByMsg[i]?.length > 0 && (
                      <div className="mt-3 pt-3 border-t hairline">
                        <p className="font-mono-brand text-propertic-muted mb-2 flex items-center gap-1.5">
                          <Globe className="w-3 h-3" /> Live sources
                        </p>
                        <div className="flex flex-wrap gap-2">
                          {groundingByMsg[i].slice(0, 6).map((s, si) => (
                            <a
                              key={si}
                              href={s.uri}
                              target="_blank"
                              rel="noreferrer"
                              className="inline-flex items-center gap-1.5 text-[11px] text-propertic-navy bg-propertic-sand hover:bg-propertic-gold hover:text-white px-3 py-1.5 rounded-full transition-colors max-w-[220px] truncate"
                              title={s.title}
                            >
                              <ExternalLink className="w-2.5 h-2.5 flex-shrink-0" />
                              <span className="truncate">{s.title || new URL(s.uri).hostname}</span>
                            </a>
                          ))}
                        </div>
                      </div>
                    )}
                    {/* Message actions bar — only on substantive replies
                        that are done streaming (skip while streaming or on
                        the initial empty assistant placeholder). */}
                    {m.content && (!streaming || i !== messages.length - 1) && (
                      <MessageActions
                        text={m.content}
                        sessionId={sessionId}
                        messageIndex={i}
                        lang={lang}
                      />
                    )}
                  </div>
                )}
              </div>
            ))}
            {messages.length === 1 && !streaming && (
              <div className="flex flex-wrap gap-2 pt-2" data-testid="chat-suggestions">
                {suggestions.map((s) => (
                  <button
                    key={s}
                    onClick={() => sendMessage(s)}
                    data-testid={`chat-suggestion-${s.slice(0, 12)}`}
                    className="text-xs text-propertic-navy border hairline hover:border-propertic-gold hover:bg-propertic-sand px-3.5 py-2 rounded-full transition-all bg-white"
                  >
                    {s}
                  </button>
                ))}
              </div>
            )}
            {/* ================================================
                CTA ROUTING (mutually exclusive per product rules)
                lead_form  → premium consultation form ONLY
                whatsapp   → nothing rendered (kept for backend analytics only)
                none       → nothing rendered
                ================================================ */}
            {ctaMode === "lead_form" && !showLead && (
              <div className="bg-propertic-sand border border-propertic-gold/30 rounded-2xl p-4 flex flex-col gap-3" data-testid="chat-cta-lead-form">
                <p className="text-sm text-propertic-navy font-editorial">{t("chat.cta_line")}</p>
                <button
                  onClick={() => setShowLead(true)}
                  data-testid="chat-cta-btn"
                  className="btn-gold rounded-full px-5 h-10 text-xs font-mono-brand inline-flex items-center gap-2 self-start"
                >
                  <Sparkles className="w-3.5 h-3.5" />
                  {t("chat.cta_btn")}
                </button>
              </div>
            )}
            {showLead && (
              <div className="bg-white border hairline rounded-2xl p-4 space-y-4" data-testid="chat-lead-form">
                <input data-testid="chat-lead-name" placeholder={`${t("consultation.full_name")} *`} value={leadForm.full_name} onChange={(e) => setLeadForm((f) => ({ ...f, full_name: e.target.value }))} className="w-full bg-white rounded-xl border hairline h-11 px-4 text-propertic-navy text-sm focus:border-propertic-gold outline-none" />
                <PhoneWithCountry
                  label={`${t("consultation.phone")} *`}
                  testid="chat-lead-phone"
                  value={leadForm.phone}
                  onChange={(e) => setLeadForm((f) => ({ ...f, phone: e.target.value }))}
                  required
                />
                <label className="flex items-start gap-2.5 cursor-pointer select-none">
                  <input
                    type="checkbox"
                    checked={leadAgreed}
                    onChange={(e) => setLeadAgreed(e.target.checked)}
                    data-testid="chat-lead-agree"
                    className="mt-0.5 w-4 h-4 shrink-0 accent-propertic-gold cursor-pointer"
                  />
                  <span className="text-[11px] text-propertic-muted leading-[1.5]">
                    {t("consultation.agree_prefix")}{" "}
                    <Link to={lh("/terms")} target="_blank" className="text-propertic-gold underline">{t("consultation.agree_terms")}</Link>
                    {" "}{t("consultation.agree_and")}{" "}
                    <Link to={lh("/privacy")} target="_blank" className="text-propertic-gold underline">{t("consultation.agree_privacy")}</Link>.
                  </span>
                </label>
                <button onClick={submitLead} disabled={!leadAgreed} data-testid="chat-lead-submit" className="btn-gold rounded-full w-full h-10 text-xs font-mono-brand disabled:opacity-40 disabled:cursor-not-allowed">
                  {t("consultation.submit")}
                </button>
                <p
                  className="text-[11px] text-propertic-muted text-center pt-1"
                  data-testid="chat-lead-whatsapp-hint"
                >
                  {lang === "ar"
                    ? "أو تواصل معنا عبر واتساب, +971 56 300 3311"
                    : "Or reach us on WhatsApp, +971 56 300 3311"}
                </p>
              </div>
            )}
            {/* Standalone legal Note footer — ALWAYS the last block in
                the thread. Extracted from the latest assistant message
                that contains one and rendered here so the consultation
                form / CTA card sits ABOVE it. Italic, muted, short.
                Sequence rule: only render the Note when the LATEST
                assistant message has finished with a Note. If a user
                just asked a new question (last message is a user turn)
                OR the latest assistant message is still streaming
                without its Note, hide the previous turn's Note so it
                doesn't linger over the pending answer. */}
            {(() => {
              let latestNote = null;
              for (let i = messages.length - 1; i >= 0; i--) {
                const mm = messages[i];
                if (mm.role === "user") {
                  // User asked a new question — hide the previous Note
                  // until the assistant answers with its own Note.
                  break;
                }
                if (mm.content) {
                  // Inspect ONLY the latest content-bearing assistant
                  // message; do not fall back to older answers.
                  const { note } = extractNote(mm.content);
                  if (note) latestNote = note;
                  break;
                }
                // Skip UI cards (share_roni / roni_install) that have
                // no `content` field and keep scanning backwards.
              }
              if (!latestNote) return null;
              return (
                <div
                  data-testid="chat-legal-note"
                  className="mt-2 pt-3 border-t hairline text-[12px] lg:text-[13px] italic text-propertic-muted leading-relaxed"
                >
                  {latestNote}
                </div>
              );
            })()}
            <div ref={bottomRef} />
          </div>

          {chatError && !streaming && (
            <div
              className="mx-3 mb-2 rounded-xl border border-red-200 bg-red-50 px-3 py-2.5 flex items-center justify-between gap-3"
              data-testid="chat-error-recovery"
            >
              <p className="text-[12px] text-red-800 leading-snug">
                {lang === "ar"
                  ? "تعذّر الوصول إلى المستشار الذكي. يمكنكم إعادة تشغيل المحادثة أدناه."
                  : "The AI advisor couldn't respond. Reset the chat below and try again."}
              </p>
              <button
                type="button"
                onClick={() => {
                  setMessages([{ role: "assistant", content: t("chat.opener") }]);
                  setSessionId("");
                  setUserTurns(0);
                  setChatError(false);
                  setShowCTA(false);
                  setCtaMode("none");
                  setShowLead(false);
                  toast.success(lang === "ar" ? "تمّت إعادة تشغيل المحادثة" : "Conversation reset");
                }}
                data-testid="chat-reset-btn"
                className="shrink-0 inline-flex items-center gap-1.5 h-8 px-3 rounded-full bg-red-700 text-white text-[11px] font-mono-brand tracking-[0.14em] uppercase hover:bg-red-800"
              >
                {lang === "ar" ? "إعادة تشغيل روني" : "Reboot Roni"}
              </button>
            </div>
          )}

          <div className="border-t hairline p-3 flex items-center gap-2 bg-propertic-paper-2 pb-[max(0.75rem,env(safe-area-inset-bottom))]">
            <input
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && sendMessage(input)}
              placeholder={t("chat.placeholder")}
              data-testid="chat-input"
              disabled={streaming}
              className="flex-1 min-w-0 bg-white rounded-full border hairline px-4 sm:px-5 h-11 lg:h-12 text-[15px] sm:text-sm lg:text-base text-propertic-navy focus:border-propertic-gold outline-none transition-colors"
            />
            <button
              onClick={() => sendMessage(input)}
              disabled={streaming || !input.trim()}
              data-testid="chat-send-btn"
              className="shrink-0 bg-gradient-to-br from-propertic-gold-2 to-propertic-gold text-propertic-navy rounded-full h-11 w-11 grid place-items-center disabled:opacity-50 hover:from-propertic-gold hover:to-propertic-gold-2 transition-colors"
              aria-label={lang === "ar" ? "إرسال" : "send"}
            >
              <Send className="w-4 h-4" />
            </button>
          </div>
        </div>
      )}
    </>
  );
}

/**
 * RoniInstallMessage, inline chat bubble that explains the PWA and shows
 * platform-specific install steps. Replaces the previous popup modal so
 * the flow feels like Roni is answering, not a system dialog.
 */
function RoniInstallMessage({ lang, platform, canFireNativePrompt, onConfirm, onDismiss }) {
  const isAr = lang === "ar";
  return (
    <div className="flex items-start gap-2.5" data-testid="roni-install-message">
      <div className="w-8 h-8 rounded-full bg-gradient-to-br from-propertic-gold-2 to-propertic-gold grid place-items-center shrink-0 shadow-inner">
        <span className="font-editorial text-propertic-navy text-sm leading-none">R</span>
      </div>
      <div className="flex-1 min-w-0 bg-white border border-propertic-gold/30 rounded-2xl rounded-ss-md p-4 shadow-sm">
        <p className="font-mono-brand text-[9px] tracking-[0.22em] uppercase text-propertic-gold mb-2">Roni</p>
        <p className="text-sm text-propertic-ink/95 leading-relaxed mb-3">
          {isAr
            ? "روني تطبيق ويب يمكن إضافته إلى الشاشة الرئيسية دون تنزيل تطبيق من متجر التطبيقات. يستخدم موارد المتصفح وقد يحفظ تفضيلاتك محلياً؛ راجع سياسة الخصوصية لمعرفة كيفية معالجة البيانات التي تشاركها معه. إضافته إلى الشاشة الرئيسية تجعل الوصول بلمسة واحدة."
            : "I'm a Web App (PWA), I don't install on your phone, consume storage, or access your device. Adding me to your home screen just makes access one-tap instead of typing the URL every time."}
        </p>
        {platform === "android" && (
          <div className="rounded-xl bg-propertic-sand/60 border border-propertic-gold/30 p-3">
            <p className="font-mono-brand text-[9px] tracking-[0.2em] uppercase text-propertic-gold mb-2">{isAr ? "أندرويد" : "Android"}</p>
            <p className="text-[13px] mb-3">
              {isAr ? "اضغط تأكيد وسيقوم متصفحك بإضافتي تلقائياً." : "Tap Confirm, your browser will add me automatically."}
            </p>
            <button
              onClick={onConfirm}
              data-testid="roni-install-android-confirm"
              className="btn-gold rounded-full w-full h-10 text-[11px] font-mono-brand tracking-[0.2em] uppercase inline-flex items-center justify-center gap-2"
            >
              <span>{isAr ? "تأكيد" : "Confirm"}</span>
            </button>
            {!canFireNativePrompt && (
              <p className="mt-2 text-[11px] text-propertic-muted">
                {isAr
                  ? "إذا لم يعمل الزر: افتح قائمة المتصفح ⋮ ← \"إضافة إلى الشاشة الرئيسية\"."
                  : "If the button doesn't work: open browser menu ⋮ → \"Add to Home Screen\"."}
              </p>
            )}
          </div>
        )}
        {platform === "ios" && (
          <div className="rounded-xl bg-propertic-sand/60 border border-propertic-gold/30 p-3">
            <p className="font-mono-brand text-[9px] tracking-[0.2em] uppercase text-propertic-gold mb-3">{isAr ? "آيفون / آيباد (Safari)" : "iPhone / iPad (Safari)"}</p>
            <ol className="space-y-2.5 text-[13px]">
              <li className="flex items-start gap-2.5"><span className="shrink-0 w-5 h-5 grid place-items-center rounded-full bg-propertic-gold text-propertic-navy font-mono-brand text-[10px] font-bold">1</span><span>{isAr ? "في شريط عنوان Safari، اضغط زر المشاركة" : "In Safari's address bar, tap the Share button"}</span></li>
              <li className="flex items-start gap-2.5"><span className="shrink-0 w-5 h-5 grid place-items-center rounded-full bg-propertic-gold text-propertic-navy font-mono-brand text-[10px] font-bold">2</span><span>{isAr ? "مرِّر إلى الأسفل" : "Scroll down"}</span></li>
              <li className="flex items-start gap-2.5"><span className="shrink-0 w-5 h-5 grid place-items-center rounded-full bg-propertic-gold text-propertic-navy font-mono-brand text-[10px] font-bold">3</span><span>{isAr ? "اضغط \"Add to Home Screen\"" : "Tap \"Add to Home Screen\""}</span></li>
              <li className="flex items-start gap-2.5"><span className="shrink-0 w-5 h-5 grid place-items-center rounded-full bg-propertic-gold text-propertic-navy font-mono-brand text-[10px] font-bold">4</span><span>{isAr ? "اضغط \"Add\", انتهيت!" : "Tap \"Add\", done!"}</span></li>
            </ol>
          </div>
        )}
        {platform === "desktop" && (
          <div className="rounded-xl bg-propertic-sand/60 border border-propertic-gold/30 p-3">
            <p className="font-mono-brand text-[9px] tracking-[0.2em] uppercase text-propertic-gold mb-2">{isAr ? "سطح المكتب" : "Desktop"}</p>
            {canFireNativePrompt ? (
              <button onClick={onConfirm} data-testid="roni-install-desktop-confirm" className="btn-gold rounded-full w-full h-10 text-[11px] font-mono-brand tracking-[0.2em] uppercase">
                {isAr ? "تثبيت روني" : "Install Roni"}
              </button>
            ) : (
              <p className="text-[13px]">
                {isAr ? "على Chrome/Edge: ابحث عن أيقونة التثبيت في شريط العنوان." : "On Chrome/Edge: look for the install icon in the address bar."}
              </p>
            )}
          </div>
        )}
        <button onClick={onDismiss} className="mt-3 text-[11px] font-mono-brand tracking-[0.16em] uppercase text-propertic-muted hover:text-propertic-navy transition-colors">
          {isAr ? "إغلاق" : "Dismiss"}
        </button>
      </div>
    </div>
  );
}
